// services/admin/AdminService.js - ReactJS (web)
// Admin Authentication & Management - Login and Admin User Management

// ✅ FIXED: Correct import path for api
import api from '../api/api';

class AdminService {
  constructor() {
    // Store api reference
    this.api = api;
    
    // Token keys for localStorage - ADMIN SPECIFIC
    // Using 'admin_' prefix to avoid conflicts with provider tokens
    this.TOKEN_KEYS = {
      ACCESS_TOKEN: 'admin_access_token',
      REFRESH_TOKEN: 'admin_refresh_token',
      USER_DATA: 'admin_user_data',
      TOKEN_EXPIRY: 'admin_token_expiry',
      USER_TYPE: 'admin_user_type', // To identify this is admin data
    };
    
    // Cache keys for localStorage
    this.CACHE_KEYS = {
      ADMIN_LIST: 'admin_list_cache',
      ADMIN_DETAIL: 'admin_detail_cache',
      ADMIN_STATS: 'admin_stats_cache',
      ADMIN_ACTIVITY: 'admin_activity_cache',
      PROVIDER_LIST: 'admin_provider_list_cache',
      PROVIDER_DETAIL: 'admin_provider_detail_cache',
    };
    
    // Cache expiration times (in milliseconds)
    this.CACHE_EXPIRY = {
      SHORT: 5 * 60 * 1000,    // 5 minutes
      MEDIUM: 15 * 60 * 1000,  // 15 minutes
      LONG: 60 * 60 * 1000,    // 1 hour
      EXTRA_LONG: 24 * 60 * 60 * 1000 // 24 hours
    };
  }

  // ==================== AUTHENTICATION METHODS ====================

  /**
   * Login admin
   * POST /api/admin/login
   */
  async login(email, password) {
    try {
      const response = await this.api.post('/admin/login', { email, password });
      
      if (response.data && response.data.success) {
        const { accessToken, refreshToken, admin } = response.data.data;
        
        // Store admin tokens and user data with admin prefix
        this.setAccessToken(accessToken);
        this.setRefreshToken(refreshToken);
        this.setUserData(admin);
        this.setTokenExpiry(Date.now() + 7 * 24 * 60 * 60 * 1000); // 7 days
        this.setUserType('admin'); // Mark as admin user
        
        // Clear any old caches on login
        this.clearAllCache();
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Logout admin
   * POST /api/admin/logout
   */
  async logout() {
    try {
      const accessToken = this.getAccessToken();
      
      if (accessToken) {
        // Call logout API
        await this.api.post('/admin/logout', {}, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
      }
      
      // Clear all local storage data (admin only)
      this.clearAllAuthData();
      this.clearAllCache();
      
      return { success: true, message: 'Logged out successfully' };
    } catch (error) {
      // Clear local data even if API call fails
      this.clearAllAuthData();
      this.clearAllCache();
      
      throw this.handleError(error);
    }
  }

  /**
   * Refresh admin access token
   * POST /api/admin/refresh-token
   */
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await this.api.post('/admin/refresh-token', { refreshToken });
      
      if (response.data && response.data.success) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data;
        
        // Update tokens
        this.setAccessToken(accessToken);
        if (newRefreshToken) {
          this.setRefreshToken(newRefreshToken);
        }
        this.setTokenExpiry(Date.now() + 7 * 24 * 60 * 60 * 1000);
        
        return response.data;
      }
      
      throw new Error('Failed to refresh token');
    } catch (error) {
      // If refresh fails, clear auth data and redirect to login
      this.clearAllAuthData();
      throw this.handleError(error);
    }
  }

  /**
   * Request password reset
   * POST /api/admin/password-reset/request
   */
  async requestPasswordReset(email) {
    try {
      const response = await this.api.post('/admin/password-reset/request', { email });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Reset password using token
   * POST /api/admin/password-reset/reset
   */
  async resetPassword(token, newPassword, confirmPassword) {
    try {
      const response = await this.api.post('/admin/password-reset/reset', {
        token,
        newPassword,
        confirmPassword
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Verify admin email
   * POST /api/admin/verify-email
   */
  async verifyEmail(token) {
    try {
      const response = await this.api.post('/admin/verify-email', { token });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired() {
    const expiry = localStorage.getItem(this.TOKEN_KEYS.TOKEN_EXPIRY);
    if (!expiry) return true;
    return Date.now() > parseInt(expiry);
  }

  /**
   * Auto-refresh token if needed
   */
  async ensureValidToken() {
    if (this.isTokenExpired() && this.getRefreshToken()) {
      try {
        await this.refreshToken();
        return true;
      } catch (error) {
        return false;
      }
    }
    return this.isAuthenticated();
  }

  /**
   * Check if admin is authenticated
   */
  isAuthenticated() {
    const userType = this.getUserType();
    return userType === 'admin' && !!this.getAccessToken() && !this.isTokenExpired();
  }

  /**
   * Check if user is admin
   */
  isAdmin() {
    const userData = this.getUserData();
    return userData?.role === 'admin' && this.getUserType() === 'admin';
  }

  // ==================== TOKEN HELPER METHODS ====================

  /**
   * Get admin access token from localStorage
   */
  getAccessToken() {
    try {
      return localStorage.getItem(this.TOKEN_KEYS.ACCESS_TOKEN);
    } catch (error) {
      console.error('Error getting admin access token:', error);
      return null;
    }
  }

  /**
   * Set admin access token in localStorage
   */
  setAccessToken(token) {
    try {
      localStorage.setItem(this.TOKEN_KEYS.ACCESS_TOKEN, token);
      return true;
    } catch (error) {
      console.error('Error setting admin access token:', error);
      return false;
    }
  }

  /**
   * Get admin refresh token from localStorage
   */
  getRefreshToken() {
    try {
      return localStorage.getItem(this.TOKEN_KEYS.REFRESH_TOKEN);
    } catch (error) {
      console.error('Error getting admin refresh token:', error);
      return null;
    }
  }

  /**
   * Set admin refresh token in localStorage
   */
  setRefreshToken(token) {
    try {
      localStorage.setItem(this.TOKEN_KEYS.REFRESH_TOKEN, token);
      return true;
    } catch (error) {
      console.error('Error setting admin refresh token:', error);
      return false;
    }
  }

  /**
   * Get admin user data from localStorage
   */
  getUserData() {
    try {
      const userData = localStorage.getItem(this.TOKEN_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error getting admin user data:', error);
      return null;
    }
  }

  /**
   * Set admin user data in localStorage
   */
  setUserData(userData) {
    try {
      localStorage.setItem(this.TOKEN_KEYS.USER_DATA, JSON.stringify(userData));
      return true;
    } catch (error) {
      console.error('Error setting admin user data:', error);
      return false;
    }
  }

  /**
   * Get admin user type
   */
  getUserType() {
    try {
      return localStorage.getItem(this.TOKEN_KEYS.USER_TYPE);
    } catch (error) {
      console.error('Error getting admin user type:', error);
      return null;
    }
  }

  /**
   * Set admin user type
   */
  setUserType(type) {
    try {
      localStorage.setItem(this.TOKEN_KEYS.USER_TYPE, type);
      return true;
    } catch (error) {
      console.error('Error setting admin user type:', error);
      return false;
    }
  }

  /**
   * Set admin token expiry in localStorage
   */
  setTokenExpiry(expiry) {
    try {
      localStorage.setItem(this.TOKEN_KEYS.TOKEN_EXPIRY, String(expiry));
      return true;
    } catch (error) {
      console.error('Error setting admin token expiry:', error);
      return false;
    }
  }

  /**
   * Clear all admin authentication data
   * Only clears admin-specific keys, not provider tokens
   */
  clearAllAuthData() {
    try {
      Object.values(this.TOKEN_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      console.log('✅ Admin auth data cleared successfully');
      return true;
    } catch (error) {
      console.error('Error clearing admin auth data:', error);
      return false;
    }
  }

  // ==================== CACHE HELPER METHODS ====================

  /**
   * Set item in localStorage with timestamp
   */
  setCacheItem(key, data, expiry = this.CACHE_EXPIRY.MEDIUM) {
    try {
      const cacheItem = {
        data,
        timestamp: Date.now(),
        expiry
      };
      localStorage.setItem(key, JSON.stringify(cacheItem));
      return true;
    } catch (error) {
      console.error('Error setting cache item:', error);
      return false;
    }
  }

  /**
   * Get item from localStorage if not expired
   */
  getCacheItem(key) {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;

      const cacheItem = JSON.parse(item);
      const now = Date.now();
      
      // Check if cache has expired
      if (now - cacheItem.timestamp > cacheItem.expiry) {
        localStorage.removeItem(key);
        return null;
      }

      return cacheItem.data;
    } catch (error) {
      console.error('Error getting cache item:', error);
      return null;
    }
  }

  /**
   * Clear specific cache item
   */
  clearCacheItem(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error clearing cache item:', error);
      return false;
    }
  }

  /**
   * Clear all admin cache
   */
  clearAllCache() {
    try {
      Object.values(this.CACHE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      console.error('Error clearing all cache:', error);
      return false;
    }
  }

  /**
   * Invalidate cache for a specific key or pattern
   */
  invalidateCache(keyPattern) {
    try {
      Object.keys(this.CACHE_KEYS).forEach(key => {
        if (key.includes(keyPattern) || key === keyPattern) {
          localStorage.removeItem(this.CACHE_KEYS[key]);
        }
      });
      return true;
    } catch (error) {
      console.error('Error invalidating cache:', error);
      return false;
    }
  }

  /**
   * Check if admin session is still valid
   * This clears invalid sessions
   */
  validateSession() {
    const userType = this.getUserType();
    const accessToken = this.getAccessToken();
    const userData = this.getUserData();
    
    // If there's no admin data, session is invalid
    if (!userType || userType !== 'admin') {
      this.clearAllAuthData();
      return false;
    }
    
    // If token is expired, try to refresh
    if (this.isTokenExpired()) {
      if (this.getRefreshToken()) {
        // Try to refresh asynchronously, but return false for now
        // The caller should handle this
        return false;
      }
      this.clearAllAuthData();
      return false;
    }
    
    return true;
  }

  // ==================== PROVIDER MANAGEMENT (ADMIN) ====================

  /**
   * Get all providers with filtering (Admin view)
   * GET /api/providers
   */
  async getAllProviders(params = {}, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `${this.CACHE_KEYS.PROVIDER_LIST}_${JSON.stringify(params)}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get('/providers', { params });
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.SHORT);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get provider by ID (Admin view)
   * GET /api/providers/:id
   */
  async getProviderById(id, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `${this.CACHE_KEYS.PROVIDER_DETAIL}_${id}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/providers/${id}`);
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update provider verification status
   * PATCH /api/providers/:id/verification
   */
  async updateProviderVerificationStatus(id, status, reason = null) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.patch(`/providers/${id}/verification`, { status, reason });
      
      // Invalidate provider caches
      this.invalidateCache('PROVIDER_LIST');
      this.invalidateCache('PROVIDER_DETAIL');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete provider (Admin)
   * DELETE /api/providers/:id
   */
  async deleteProvider(id) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.delete(`/providers/${id}`);
      
      // Invalidate provider caches
      this.invalidateCache('PROVIDER_LIST');
      this.invalidateCache('PROVIDER_DETAIL');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Toggle provider featured status
   * PATCH /api/providers/:id/featured
   */
  async toggleProviderFeatured(id, isFeatured, duration = 30) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.patch(`/providers/${id}/featured`, { isFeatured, duration });
      
      // Invalidate provider caches
      this.invalidateCache('PROVIDER_LIST');
      this.invalidateCache('PROVIDER_DETAIL');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get provider statistics (Admin)
   * GET /api/providers/:id/statistics
   */
  async getProviderStatistics(id, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `provider_stats_${id}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/providers/${id}/statistics`);
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by user ID (Admin can view any user's providers)
   * GET /api/providers/user/:userId
   */
  async getProvidersByUserId(userId, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `user_providers_${userId}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/providers/user/${userId}`);
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Bulk update provider status (Admin)
   * PATCH /api/providers/bulk/status
   */
  async bulkUpdateProviderStatus(ids, status, reason = null) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.patch('/providers/bulk/status', { ids, status, reason });
      
      // Invalidate provider caches
      this.invalidateCache('PROVIDER_LIST');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PROFILE IMAGE METHODS ====================

  /**
   * Upload admin profile image
   * POST /api/admin/:id/profile-image
   */
  async uploadProfileImage(id, file) {
    try {
      await this.ensureValidToken();
      
      const formData = new FormData();
      formData.append('profileImage', file);

      const response = await this.api.post(`/admin/${id}/profile-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Update user data if it's the current user
      if (response.data && response.data.success) {
        const userData = this.getUserData();
        if (userData && response.data.data) {
          this.setUserData({ ...userData, ...response.data.data });
        }
        this.invalidateCache('ADMIN_DETAIL');
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Remove admin profile image
   * DELETE /api/admin/:id/profile-image
   */
  async removeProfileImage(id) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.delete(`/admin/${id}/profile-image`);
      
      // Update user data if it's the current user
      if (response.data && response.data.success) {
        const userData = this.getUserData();
        if (userData && response.data.data) {
          this.setUserData({ ...userData, ...response.data.data });
        }
        this.invalidateCache('ADMIN_DETAIL');
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== ADMIN MANAGEMENT ROUTES ====================

  /**
   * Get all admins with filters
   * GET /api/admins
   */
  async getAllAdmins(params = {}, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `${this.CACHE_KEYS.ADMIN_LIST}_${JSON.stringify(params)}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get('/admins', { params });
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get admin with full details
   * GET /api/admin/:id/full
   */
  async getAdminFullDetails(id, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `${this.CACHE_KEYS.ADMIN_DETAIL}_${id}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/admin/${id}/full`);
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Create new admin
   * POST /api/admin
   */
  async createNewAdmin(adminData) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.post('/admin', adminData);
      
      // Invalidate admin list cache
      this.invalidateCache('ADMIN_LIST');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update admin profile
   * PUT /api/admin/:id/profile
   */
  async updateAdminProfile(id, updateData) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.put(`/admin/${id}/profile`, updateData);
      
      // Invalidate admin caches
      this.invalidateCache('ADMIN_DETAIL');
      this.invalidateCache('ADMIN_LIST');
      
      // Update user data if it's the current user
      const userData = this.getUserData();
      if (userData && userData.id === id) {
        this.setUserData({ ...userData, ...updateData });
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Delete admin
   * DELETE /api/admin/:id
   */
  async deleteAdmin(id) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.delete(`/admin/${id}`);
      
      // Invalidate admin caches
      this.invalidateCache('ADMIN_DETAIL');
      this.invalidateCache('ADMIN_LIST');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Suspend admin
   * POST /api/admin/:id/suspend
   */
  async suspendAdmin(id, reason = '') {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.post(`/admin/${id}/suspend`, { reason });
      
      // Invalidate admin caches
      this.invalidateCache('ADMIN_DETAIL');
      this.invalidateCache('ADMIN_LIST');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Activate admin
   * POST /api/admin/:id/activate
   */
  async activateAdmin(id) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.post(`/admin/${id}/activate`);
      
      // Invalidate admin caches
      this.invalidateCache('ADMIN_DETAIL');
      this.invalidateCache('ADMIN_LIST');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get admin activity log
   * GET /api/admin/:id/activity
   */
  async getAdminActivityLog(id, limit = 50, skip = 0, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `${this.CACHE_KEYS.ADMIN_ACTIVITY}_${id}_${limit}_${skip}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/admin/${id}/activity`, {
        params: { limit, skip }
      });
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.SHORT);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Clear admin activity log
   * DELETE /api/admin/:id/activity
   */
  async clearAdminActivityLog(id) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.delete(`/admin/${id}/activity`);
      
      // Invalidate activity cache
      this.invalidateCache('ADMIN_ACTIVITY');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get admin activity summary
   * GET /api/admin/:id/activity-summary
   */
  async getAdminActivitySummary(id, days = 30, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `${this.CACHE_KEYS.ADMIN_ACTIVITY}_summary_${id}_${days}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/admin/${id}/activity-summary`, {
        params: { days }
      });
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.SHORT);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update admin statistics
   * POST /api/admin/:id/stats
   */
  async updateAdminStats(id, stats) {
    try {
      await this.ensureValidToken();
      
      const response = await this.api.post(`/admin/${id}/stats`, stats);
      
      // Invalidate stats cache
      this.invalidateCache('ADMIN_STATS');
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get admin statistics
   * GET /api/admin/:id/stats
   */
  async getAdminStatistics(id, useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `${this.CACHE_KEYS.ADMIN_STATS}_${id}`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get(`/admin/${id}/stats`);
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.MEDIUM);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get admin stats summary
   * GET /api/admins/stats/summary
   */
  async getAdminStatsSummary(useCache = true) {
    try {
      await this.ensureValidToken();
      
      const cacheKey = `${this.CACHE_KEYS.ADMIN_STATS}_summary`;
      
      if (useCache) {
        const cachedData = this.getCacheItem(cacheKey);
        if (cachedData) {
          return cachedData;
        }
      }

      const response = await this.api.get('/admins/stats/summary');
      
      if (response.data && response.data.success !== false) {
        this.setCacheItem(cacheKey, response.data, this.CACHE_EXPIRY.SHORT);
      }
      
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    // If unauthorized, clear auth data
    if (error.response && error.response.status === 401) {
      this.clearAllAuthData();
    }
    
    if (error.response) {
      return {
        success: false,
        statusCode: error.response.status,
        message: error.response.data?.message || error.response.data?.error || 'An error occurred',
        data: error.response.data,
      };
    } else if (error.request) {
      return {
        success: false,
        message: 'No response from server. Please check your connection.',
        error: error.request,
      };
    } else {
      return {
        success: false,
        message: error.message || 'An unexpected error occurred',
        error: error,
      };
    }
  }

  /**
   * Get admin statuses (helper for filtering)
   */
  getAdminStatuses() {
    return ['active', 'suspended', 'inactive'];
  }

  /**
   * Get admin roles (helper for filtering)
   */
  getAdminRoles() {
    return ['admin'];
  }

  /**
   * Get verification statuses (helper for filtering)
   */
  getVerificationStatuses() {
    return ['pending', 'verified', 'rejected', 'suspended'];
  }

  /**
   * Get service types (helper for filtering)
   */
  getServiceTypes() {
    return [
      'Electrician', 'Plumber', 'Mason', 'Painter', 'Carpenter',
      'Barber', 'Hairdresser', 'Mechanic', 'HVAC Technician',
      'Interior Designer', 'Landscaper', 'Cleaner', 'Handyman',
      'Tiler', 'Solar Installer', 'Flooring Specialist', 'Other'
    ];
  }
}

export default new AdminService();